import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { TrendUp, Eye, EyeSlash, ChartLineUp, CurrencyBtc, CurrencyDollar, MagnifyingGlass, Brain, Wallet, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import GettingStarted from "@/components/GettingStarted";

const BG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9ufGVufDB8fHx8MTc4NjM4MTgyNnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !accepted) { toast.error("Please accept the Terms & Risk Disclaimer to continue"); return; }
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      toast.success("Welcome to MarketPilot AI");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Authentication failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background overflow-y-auto">
      <div className="flex min-h-screen">
      <div className="relative hidden lg:flex w-[52%] flex-col justify-between border-r border-border overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="relative z-10 p-10">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center bg-volt rounded-sm">
              <TrendUp size={22} weight="bold" className="text-white" />
            </div>
            <span className="font-heading font-bold text-lg">MarketPilot<span className="text-volt"> AI</span></span>
          </div>
        </div>
        <div className="relative z-10 p-10">
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tighter">
            One platform for<br /><span className="text-volt ai-glow">Stocks · Crypto · Forex</span>
          </h1>
          <p className="mt-4 max-w-md text-sm text-muted-foreground">
            A probabilistic market intelligence system across three asset classes — real market data,
            AI scoring, forecast distributions, custom scanners, a paper-trading simulator, and optional
            live brokerage (Alpaca &amp; OANDA). Never predictions, only probabilities.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-2 max-w-md">
            {[[ChartLineUp, "Stocks", "AI scores & forecasts"], [CurrencyBtc, "Crypto", "24/7 momentum"], [CurrencyDollar, "Forex", "24 pairs vs DXY"]].map(([Icon, t, s]) => (
              <div key={t} className="border border-border bg-card/50 rounded-sm px-3 py-3">
                <Icon size={18} weight="fill" className="text-volt" />
                <div className="mono text-xs font-medium mt-1.5">{t}</div>
                <div className="mono text-[9px] uppercase tracking-wide text-muted-foreground mt-0.5">{s}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-3 mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="border border-border px-2 py-1">FACT</span>
            <span className="border border-dashed border-white/30 px-2 py-1">MODEL OUTPUT</span>
            <span className="border border-volt/40 px-2 py-1 text-volt">AI INTERPRETATION</span>
          </div>
          <a href="#getting-started" data-testid="scroll-to-guide" className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors duration-200">
            New here? Read the step-by-step guide ↓
          </a>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="auth-form">
          <h2 className="font-heading text-2xl font-semibold">{mode === "login" ? "Sign in" : "Create account"}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{mode === "login" ? "Access your intelligence dashboard" : "Start with a $100,000 paper portfolio"}</p>

          <div className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="label-xs">Name</Label>
                <Input data-testid="name-input" value={form.name} onChange={set("name")} required className="mt-1.5 bg-card rounded-sm" placeholder="Jane Trader" />
              </div>
            )}
            <div>
              <Label className="label-xs">Email</Label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 bg-card rounded-sm mono" placeholder="you@fund.com" />
            </div>
            <div>
              <Label className="label-xs">Password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="password-input" type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} required className="bg-card rounded-sm mono pr-10" placeholder="••••••••" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors duration-200">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          {mode === "register" && (
            <label className="mt-5 flex items-start gap-2.5 cursor-pointer" data-testid="consent-label">
              <Checkbox data-testid="consent-checkbox" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5 border-volt data-[state=checked]:bg-volt" />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I agree to the <Link to="/terms" target="_blank" className="text-volt hover:underline">Terms of Service</Link> and acknowledge the <Link to="/risk" target="_blank" className="text-volt hover:underline">Risk Disclaimer</Link> — trading carries risk and all signals are probabilities, not guarantees.
              </span>
            </label>
          )}

          <Button data-testid="auth-submit-btn" type="submit" disabled={loading || (mode === "register" && !accepted)}
            className="mt-6 w-full bg-volt hover:bg-volt/90 text-white rounded-sm h-10 font-medium disabled:opacity-50">
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>

          <button type="button" data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-white transition-colors duration-200">
            {mode === "login" ? "No account? Register" : "Have an account? Sign in"}
          </button>
        </form>
      </div>
      </div>

      {/* How it works */}
      <div className="border-t border-border" data-testid="how-it-works">
        <div className="max-w-6xl mx-auto px-8 py-14">
          <p className="mono text-[10px] uppercase tracking-widest text-volt">How MarketPilot works</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-2 max-w-2xl">From raw market data to a risk-managed trade — in four steps</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              [MagnifyingGlass, "1 · Scan", "Screen U.S. stocks, crypto, and 24 forex pairs with custom filters and live data — surfaced by AI relative-strength scores."],
              [Brain, "2 · Understand", "Every asset gets an AI score, a bull/neutral/bear signal, and a probabilistic forecast — clearly labelled as model output, never a promise."],
              [Wallet, "3 · Practice", "Test ideas risk-free in a $100,000 paper simulator with real prices, leverage, and P/L across all three asset classes."],
              [Bank, "4 · Trade Live", "When ready, connect your own Alpaca or OANDA account (BYOK) and route real orders with stops, targets, and break-even automation."],
            ].map(([Icon, t, s]) => (
              <div key={t} className="border border-border bg-card/40 rounded-sm p-5 hover:border-volt/40 transition-colors duration-200">
                <div className="flex h-10 w-10 items-center justify-center bg-volt/15 rounded-sm"><Icon size={20} weight="fill" className="text-volt" /></div>
                <div className="font-heading font-semibold mt-4">{t}</div>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step-by-step user guide */}
      <div id="getting-started" className="border-t border-border bg-[#0c0c0d]/40">
        <GettingStarted onCta={() => { setMode("register"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      </div>

      {/* Footer */}
      <footer className="border-t border-border" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center bg-volt rounded-sm"><TrendUp size={15} weight="bold" className="text-white" /></div>
            <span className="font-heading font-bold text-sm">MarketPilot<span className="text-volt"> AI</span></span>
            <span className="mono text-[10px] text-muted-foreground ml-2">Stocks · Crypto · Forex</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mono text-xs text-muted-foreground">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors duration-200">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-white transition-colors duration-200">Terms</Link>
            <Link to="/risk" data-testid="footer-risk" className="hover:text-white transition-colors duration-200">Risk Disclaimer</Link>
            <span>© {new Date().getFullYear()} MarketPilot AI</span>
          </div>
        </div>
        <p className="max-w-6xl mx-auto px-8 pb-8 mono text-[10px] leading-relaxed text-muted-foreground/70">
          Trading stocks, crypto, and forex carries substantial risk of loss and is not suitable for everyone. MarketPilot AI provides information and tools only — not financial advice. All scores and forecasts are probabilities, not guarantees. Live brokerage is provided by third parties (Alpaca, OANDA) under their own terms.
        </p>
      </footer>
    </div>
  );
}
